import React from 'react';
import PropTypes from 'prop-types';
import {
  FaHeartbeat,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCopy,
  FaTimesCircle,
  FaShieldAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import MetricKpiCard from '../MetricKpiCard';
import { formatUtcDateRange } from '../../shared/analyticsViewUtils';

export default function PipelineHealthTab({
  pipelineHealth = {},
  loading = false
}) {
  const { errors, usageSummary } = pipelineHealth || {};

  // Extract counts from telemetryErrors endpoint
  const rejectedEvents = Number(errors?.rejectedEvents || 0);
  const duplicateEvents = Number(errors?.duplicateEvents || 0);
  const topErrors = Array.isArray(errors?.topErrorReasons) ? errors.topErrorReasons : [];

  const isHealthy = rejectedEvents === 0;

  const windowStart = errors?.windowStartUtc || usageSummary?.windowStartUtc;
  const windowEnd = errors?.windowEndUtc || usageSummary?.windowEndUtc;
  const windowText = formatUtcDateRange(windowStart, windowEnd);

  return (
    <div className="tab-content-pane pipeline-health-tab" data-testid="pipeline-health-tab">
      {/* Top Status Banner */}
      <div
        className={`pipeline-status-banner ${isHealthy ? 'status-healthy' : 'status-warning'}`}
        role="status"
        data-testid="pipeline-status-banner"
      >
        <div className="status-banner-icon-wrap">
          {isHealthy ? (
            <FaCheckCircle className="status-banner-icon healthy-icon" />
          ) : (
            <FaExclamationTriangle className="status-banner-icon warning-icon" />
          )}
        </div>
        <div className="status-banner-text">
          <h4 className="status-banner-title">
            {isHealthy
              ? 'Telemetry Ingestion Pipeline Healthy'
              : `${rejectedEvents.toLocaleString()} Rejected Events Detected`}
          </h4>
          <p className="status-banner-description">
            {isHealthy
              ? 'All client batches from admin and runtime modules were successfully accepted and ingested with zero dropouts.'
              : 'Some telemetry events were rejected during ingestion. Check schema, authentication, or site collection registration.'}
          </p>
        </div>
        <div className="status-banner-badge">
          <FaShieldAlt className="shield-icon" /> {isHealthy ? 'Pipeline Active' : 'Action Required'}
        </div>
      </div>

      {/* Diagnostic KPI Cards */}
      <div className="kpi-grid">
        <MetricKpiCard
          title="Ingestion Reliability"
          value={isHealthy ? '100%' : 'Degraded'}
          subtitle={isHealthy ? '100% of telemetry data ingested' : `${rejectedEvents.toLocaleString()} dropped events`}
          icon={<FaHeartbeat />}
          color={isHealthy ? 'emerald' : 'rose'}
          loading={loading}
          tooltip="Operational health and acceptance rate of telemetry data stream"
        />

        <MetricKpiCard
          title="Rejected Events"
          value={rejectedEvents.toLocaleString()}
          subtitle={rejectedEvents === 0 ? '0 ingestion dropouts' : 'Schema or auth dropouts'}
          icon={<FaTimesCircle />}
          color={rejectedEvents === 0 ? 'emerald' : 'rose'}
          loading={loading}
          tooltip="Events dropped during ingestion due to invalid schema, missing required context, or entitlement denial"
        />

        <MetricKpiCard
          title="Duplicate Events Handled"
          value={duplicateEvents.toLocaleString()}
          subtitle="Filtered to prevent double-counting"
          icon={<FaCopy />}
          color="amber"
          loading={loading}
          tooltip="Duplicate telemetry submissions filtered out to ensure analytics counts remain accurate"
        />
      </div>

      {/* Top Runtime Error Reasons Card */}
      <div className="runtime-errors-card" data-testid="runtime-errors-card">
        <div className="table-card-header">
          <div>
            <h3 className="table-card-title">Top Runtime Error Reasons</h3>
            <p className="table-card-subtitle">Errors captured by DAP runtime and player during user sessions</p>
          </div>
          <div className="window-pill" data-testid="runtime-errors-window-pill">
            <FaCalendarAlt className="calendar-icon" /> {windowText}
          </div>
        </div>

        {topErrors.length === 0 ? (
          <div className="runtime-errors-empty" data-testid="runtime-errors-empty">
            <div className="runtime-errors-empty-icon">
              <FaCheckCircle />
            </div>
            <div className="runtime-errors-empty-text">
              <h5 className="runtime-errors-empty-title">No runtime errors reported during this period</h5>
              <p className="runtime-errors-empty-desc">All client sessions and guides executed smoothly.</p>
            </div>
          </div>
        ) : (
          <div className="runtime-errors-list" data-testid="runtime-errors-list">
            {topErrors.map((err, idx) => {
              const reasonText = typeof err === 'string' ? err : (err?.reason || err?.code || 'UNKNOWN_ERROR');
              const descText = typeof err === 'object' && err?.description ? err.description : null;
              const countVal = typeof err === 'object' && err?.count !== undefined ? Number(err.count) : null;

              return (
                <div key={`err-${idx}`} className="runtime-error-item" data-testid={`runtime-error-item-${idx}`}>
                  <span className="runtime-error-rank">#{idx + 1}</span>
                  <code className="runtime-error-code">{reasonText}</code>
                  {descText && <span className="runtime-error-desc">{descText}</span>}
                  {countVal !== null && (
                    <span className="runtime-error-count">
                      {countVal.toLocaleString()} reports
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

PipelineHealthTab.propTypes = {
  pipelineHealth: PropTypes.shape({
    errors: PropTypes.shape({
      rejectedEvents: PropTypes.number,
      duplicateEvents: PropTypes.number,
      topErrorReasons: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      ),
      windowStartUtc: PropTypes.string,
      windowEndUtc: PropTypes.string
    }),
    usageSummary: PropTypes.object,
    isEntitled: PropTypes.bool,
    error: PropTypes.any
  }),
  loading: PropTypes.bool
};
